import { useCallback, useRef, useState } from "react";

/**
 * Keyboard navigation for a table's rows, per the book's Focus and keyboard
 * section: "Arrow keys move between rows, Enter opens, and the row's primary
 * action is reachable without opening it."
 *
 * A roving tabindex, not a tab stop per row. Tab takes you to the table and
 * then out of it again; arrows move within. A table of 200 rows that put 200
 * stops in the tab order would be unusable — reaching the pagination below it
 * would mean 200 presses.
 *
 * Rows are the focus target rather than the cells: a row is what a person acts
 * on here, and the row's own controls stay reachable by tabbing into it.
 */
export const useRovingRows = () => {
  const bodyRef = useRef(null);
  const [active, setActive] = useState(0);

  const rowsIn = useCallback(
    () => Array.from(bodyRef.current?.querySelectorAll("[data-roving-row]") || []),
    []
  );

  const focusAt = useCallback(
    (index) => {
      const list = rowsIn();
      if (!list.length) return;
      const next = Math.min(list.length - 1, Math.max(0, index));
      list[next]?.focus();
      setActive(next);
    },
    [rowsIn]
  );

  const rowProps = useCallback(
    (index) => ({
      "data-roving-row": true,
      // Exactly one row is in the tab order; arrows reach the rest.
      tabIndex: index === active ? 0 : -1,
      onFocus: () => setActive(index),
      onKeyDown: (e) => {
        // Let a control inside the row have its own keys.
        if (e.target !== e.currentTarget) return;
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            focusAt(index + 1);
            break;
          case "ArrowUp":
            e.preventDefault();
            focusAt(index - 1);
            break;
          case "Home":
            e.preventDefault();
            focusAt(0);
            break;
          case "End":
            e.preventDefault();
            focusAt(rowsIn().length - 1);
            break;
          case "Enter":
            // A linked row is an anchor, so this opens it the way a click does.
            e.currentTarget.click();
            break;
          default:
        }
      },
    }),
    [active, focusAt, rowsIn]
  );

  return { bodyRef, rowProps };
};

export default useRovingRows;
